import { motion } from "framer-motion";
import { QrCode } from "lucide-react";
import { VENDING, IMG } from "@/data/menu";
import { Reveal } from "@/components/site/Reveal";

export default function VendingSection() {
  return (
    <section id="vending" data-testid="vending-section" className="bg-beige py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-brand">24/7 Vending</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-coffee">Grab &amp; go, anytime.</h2>
            <p className="mt-4 text-lg text-coffee-soft">Ready-to-drink protein, straight from the machine. Every can shows its full nutrition per serving with a live stock indicator.</p>
          </Reveal>
          <Reveal delay={1} className="mt-8 rounded-[2.5rem] overflow-hidden border border-line">
            <img src={IMG.vending} alt="ProteinCafe vending machine" className="w-full h-80 object-cover" />
          </Reveal>
          <Reveal delay={2} className="mt-6 rounded-2xl overflow-hidden border border-line">
            <img src={IMG.cans} alt="ProteinCafe cans line-up" className="w-full object-cover" />
          </Reveal>
        </div>

        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
          {VENDING.map((c, i) => (
            <motion.div
              key={c.name}
              data-testid={`vending-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
              className="rounded-3xl border border-line p-6 flex flex-col bg-white"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-[76px] h-[108px] rounded-2xl overflow-hidden bg-[#141414] border border-line/60 grid place-items-center">
                  <img src={c.image} alt={`${c.name} can`} data-testid={`vending-can-img-${i}`} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 rounded-full ${c.use === "Pre-Workout" ? "bg-[#8A2231]/10 text-teal-brand" : "bg-coffee/10 text-coffee"}`}>{c.use}</span>
                  <h3 className="mt-2 font-display text-2xl font-bold text-coffee leading-tight">{c.name}</h3>
                  <p className="text-sm text-coffee-muted">{c.flavor}</p>
                </div>
                <QrCode size={32} className="text-coffee/25 shrink-0" />
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 border-t border-line pt-4 font-mono text-sm">
                <Stat label="kcal" value={c.calories} />
                <Stat label="protein" value={`${c.protein}g`} />
                <Stat label="carbs" value={`${c.carbs}g`} />
                <Stat label="fat" value={`${c.fat}g`} />
                <Stat label="sugar" value={`${c.sugar}g`} />
                <Stat label="sodium" value={`${c.sodium}mg`} />
              </div>

              <div className="mt-5 flex items-center justify-between">
                <span className="font-mono text-xl font-bold text-coffee">₹{c.price}</span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${c.stock ? "text-teal-brand" : "text-coffee-muted"}`} data-testid={`vending-stock-${i}`}>
                  <span className={`w-2 h-2 rounded-full ${c.stock ? "bg-[#8A2231] animate-pulseDot" : "bg-coffee-muted"}`} />
                  {c.stock ? "In Stock" : "Sold Out"}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-coffee font-semibold">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-coffee-muted">{label}</div>
  </div>
);
